import qs from 'qs';

import { UiToast } from 'src/components/UI/toasts';
import { deConfigurationSettingsSelector } from 'src/store/commonPart/selector';
import { store } from 'src/store/store';
import { SafeAnyType } from 'src/utils/safeAny';

declare global {
  interface Window {
    AWS: {
      config: SafeAnyType;
      S3: SafeAnyType;
      CognitoIdentityCredentials: SafeAnyType;
    };
  }
}

export const guid = (): string => {
  const s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${s4() + s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

/** get converted url from s3 to cloudFront */
export const getSizedImageUrl = (filePath: string, customSizes?: { width: number; height: number }): string => {
  const {
    externalServices: {
      amazonS3: { cloudFrontUrl },
    },
  } = deConfigurationSettingsSelector(store.getState());

  let fileUri = filePath;

  let sizes = customSizes;

  if (filePath.startsWith('http')) {
    const temp = filePath.split('?')[0].split('/');
    fileUri = `${temp[temp.length - 2]}/${temp[temp.length - 1]}`;

    if (!customSizes) {
      const tempSizes = qs.parse(filePath.split('?')[1]);

      if (tempSizes?.width && tempSizes?.height) {
        sizes = tempSizes as unknown as { width: number; height: number };
      }
    }
  }

  return sizes ? `${cloudFrontUrl}${sizes.width}x${sizes.height}/${fileUri}` : `${cloudFrontUrl}${fileUri}`;
};

export const getPreviewImageUrl = (imageName: string): string => {
  const {
    externalServices: {
      amazonS3: { cloudFrontUrl },
    },
  } = deConfigurationSettingsSelector(store.getState());

  return `${cloudFrontUrl}preview/${imageName}`;
};

/** get unique file name for uploading */
export const getS3FileKey = (rootFolder: string, fileNamePrefix = ''): string => {
  const fileExtension = 'jpg';
  const fileNameLocal = `${guid().split('-').join('')}.${fileExtension}`;
  const prefix = '/';
  return encodeURIComponent(rootFolder) + prefix + fileNamePrefix + fileNameLocal;
};

export type S3UploadReturnType = {
  filePath: string;
  fullPath: string;
  fileName: string;
};

export type S3UploadReturnOptionalType = Partial<S3UploadReturnType>;

/** upload image to aws */
export const S3Upload = (
  body: File | Blob,
  { fileNamePrefix, fileName = '' }: { fileNamePrefix?: string; fileName?: string } = {}
): Promise<S3UploadReturnType> => {
  try {
    const { AWS } = window;

    const {
      externalServices: {
        amazonS3: { uploadedFilesBucketName, rootFolder, identityPoolId, region },
      },
    } = deConfigurationSettingsSelector(store.getState());

    AWS.config.region = region;
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: identityPoolId,
    });
    AWS.config.correctClockSkew = true;

    const s3 = new AWS.S3({
      apiVersion: '2006-03-01',
    });

    return new Promise((resolve, reject) => {
      s3.upload(
        {
          Bucket: uploadedFilesBucketName,
          ACL: 'public-read',
          Body: body,
          ContentType: 'image/jpg',
          Key: getS3FileKey(rootFolder, fileNamePrefix),
        },
        {
          partSize: 20 * 1024 * 1024,
          queueSize: 1,
        },
        (e: Error, fileData: SafeAnyType) => {
          if (e) {
            reject(e);
          } else {
            resolve({
              filePath: fileData.Key, // only file name (folder/file.ext)
              fullPath: fileData.Location, // full path (https://.../folder/file.ext)
              fileName,
            });
          }
        }
      );
    });
  } catch (e) {
    UiToast.error('Unable to call AWS-SDK');

    return new Promise((resolve, reject) => {
      reject(Error('Unable to call AWS-SDK'));
    });
  }
};
